import algoliasearch from 'algoliasearch/lite';

import { buildRelativePath } from '@/src/utilities/url';

export const newYorkCities = [
  'greece',
  'paris',
  'rome',
  'london',
  'berlin',
  'kent',
  'madrid',
  'troy',
  'geneva',
  'warsaw',
  'york',
  'oxford',
  'athens',
  'mexico',
  'poland',
  'panama',
  'alabama',
  'amsterdam',
  'phoenicia',
  'china',
  'italy',
  'atlanta',
  'cleveland',
  'delhi',
  'naples',
  'cuba',
  'cambridge',
  'belfast',
  'lisbon',
  'rotterdam',
  'medina',
  'dublin',
];

export type RouteState = {
  term: string | undefined;
  zipcode: string | undefined;
  distance: number | 'all' | undefined;
  sortBy: string | undefined;
  locationTypes: string[] | undefined;
  relatedConditions: string[] | undefined;
  relatedDepartments: string[] | undefined;
  page: number | undefined;
};

export function shortenZipcode(userAddress: string): string {
  // check if userAddress is longer than 5 characters
  // the "zipcode" is the first 5 characters of the userAddress
  const zipcode =
    userAddress.length > 5 ? userAddress.slice(0, 5) : userAddress;

  // check if all characters are numbers
  // if not, the user entered text (eg a city name) instead of numbers
  const isValidZipcode = zipcode
    .split('')
    .every((char) => !isNaN(Number(char)));

  // if the zipcode is valid, return it
  // otherwise, return the userAddress
  if (isValidZipcode) {
    return zipcode;
  } else {
    return userAddress;
  }
}

// load config functions:
export function loadConfig(): any {
  const url = location.href;
  const urlParts = location.href.match(/^(.*?)\/search/);
  const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

  const config = url.endsWith('/conditions-and-treatments')
    ? window['condition-landing-search']
    : url.endsWith('/people')
    ? window['provider-landing-search']
    : url.endsWith('/locations')
    ? window['location-landing-search']
    : baseUrl.endsWith('/conditions-and-treatments/')
    ? window['condition-search']
    : baseUrl.endsWith('/people/')
    ? window['provider-search']
    : baseUrl.endsWith('/locations/')
    ? window['location-search']
    : window['header-search'];

  if (!config) {
    return;
  }

  return config;
}

// function getSearchConfig()

export function loadClient(): any {
  loadConfig();
  const searchClient = algoliasearch(
    loadConfig().applicationId,
    loadConfig().searchKey
  );

  return searchClient;
}

export function generateSearchResultsPath(
  searchTerm: string,
  zipcode?: string
): URL | string {
  if (zipcode && zipcode.length > 0) {
    return buildRelativePath(loadConfig().searchContext.searchResultsPagePath, {
      term: searchTerm,
      zipcode: zipcode,
    })
      .toString()
      .split('%20')
      .join('+');
  } else {
    return buildRelativePath(loadConfig().searchContext.searchResultsPagePath, {
      term: searchTerm,
    })
      .toString()
      .split('%20')
      .join('+');
  }
}

export function removeUrlZipcode(): void {
  const url = new URL(window.location.href);

  if (url.searchParams.has('zipcode')) {
    url.searchParams.delete('zipcode');
  }
  window.history.replaceState({}, '', url.href);
}

export function insertUrlZipcode(): void {
  const url = new URL(window.location.href);
  const geocodeInput = document.getElementById(
    'geocodeInput'
  ) as HTMLInputElement;
  const modifiedGeoValue = newYorkCities.includes(
    geocodeInput.value.toLowerCase()
  )
    ? `${geocodeInput.value}, NY`
    : geocodeInput.value;

  url.searchParams.set('zipcode', modifiedGeoValue);
  window.history.replaceState({}, '', url.href);
}

export function getZipcodeFromUrl(): string {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('zipcode') ?? '';
}

// export function getSortByFromUrl(): string {
//   const searchParams = new URLSearchParams(window.location.search);
//   const urlParts = location.href.match(/^(.*?)\/search/);
//   const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

//   if (baseUrl.endsWith('/locations/')) {
//     if (searchParams.get('sortBy') === 'Alphabetical') {
//       return 'LOCATION NAME';
//     } else return (searchParams.get('sortBy') ?? 'location name').toUpperCase();
//   } else return 'error';
// }

export function getRoundedRadiusFromUrl(): number | 'all' | undefined {
  const searchParams = new URLSearchParams(window.location.search);
  const roundedRadius = searchParams.get('distance') ?? 'all';
  return roundedRadius === 'all' ? 'all' : parseInt(roundedRadius ?? '');
}

export function toggleDistanceAccordion(): void {
  const distanceAccordionButton = document.querySelector(
    '.distance-accordion-button'
  );
  const geocodeInput = document.getElementById(
    'geocodeInput'
  ) as HTMLInputElement;

  if (geocodeInput.value.length > 0) {
    distanceAccordionButton?.removeAttribute('disabled');
    distanceAccordionButton?.classList.remove(
      'text-gray-500/80',
      'cursor-not-allowed'
    );
    distanceAccordionButton?.classList.add('cursor-pointer');
  } else {
    distanceAccordionButton?.setAttribute('disabled', 'true');
    distanceAccordionButton?.classList.remove('cursor-pointer');
    distanceAccordionButton?.classList.add(
      'text-gray-500/80',
      'cursor-not-allowed'
    );
  }

  toggleDistanceTooltip();
}

function toggleDistanceTooltip(): void {
  const distanceAccordionButton = document.querySelector(
    '.distance-accordion-button'
  );
  const oldSvg = distanceAccordionButton?.querySelector(
    'svg'
  ) as unknown as HTMLElement;

  const arrowSvg = document.createElement('svg');
  arrowSvg.innerHTML = `<svg
    xmlns="http://www.w3.org/2000/svg"
    class="h-5 w-5 transition-all stroke-pacific-blue"
    :class="{ 'rotate-180': expandedDistance }"
    fill="none"
    viewBox="0 0 24 24"
    stroke="" stroke-width="2">
      <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7">
      </path>
    </svg>`;

  const tooltipSvg = document.createElement('svg');
  tooltipSvg.innerHTML = `<span class="relative group">
    <span class="ml-2 tooltip cursor-pointer">
      <svg
        class="w-4 h-4 fill-slate-500"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <path
          d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM256 336c-18 0-32 14-32 32s13.1 32 32 32c17.1 0 32-14 32-32S273.1 336 256 336zM289.1 128h-51.1C199 128 168 159 168 198c0 13 11 24 24 24s24-11 24-24C216 186 225.1 176 237.1 176h51.1C301.1 176 312 186 312 198c0 8-4 14.1-11 18.1L244 251C236 256 232 264 232 272V288c0 13 11 24 24 24S280 301 280 288V286l45.1-28c21-13 34-36 34-60C360 159 329 128 289.1 128z"
        />
      </svg>
    </span>
    <span role="tooltip" class="tooltip-modal z-50 tooltip-arrow-right">
      Please enter your zip code to access distance filters.
    </span>
  </span>`;

  distanceAccordionButton?.hasAttribute('disabled')
    ? oldSvg.replaceWith(tooltipSvg)
    : oldSvg.replaceWith(arrowSvg);
}

export function cleanQueryString(queryString: string): string {
  return queryString.split('%20').join('+');
}

// function to remove/insert the divider between affiliated and non-affiliated providers
export function showDivider(): void {
  const sortByDropdown = document.querySelector(
    '.ais-SortBy-select'
  ) as HTMLSelectElement;

  const firstNonAffiliated = document.querySelector('.non-affiliated');

  const affiliatedElements = document.querySelectorAll('.affiliated');
  const lastAffiliated = affiliatedElements[affiliatedElements.length - 1];
  const lastAffiliatedBeforeDivider = firstNonAffiliated
    ? lastAffiliated
    : null;

  const oldDivider = document.querySelector('.divider');

  if (oldDivider) {
    oldDivider.remove();
  }

  if (sortByDropdown?.value.endsWith('distance')) {
    // old divider has been removed
    // if results are sorted by distance, we don't add a new divider
    return;
  }

  const divider = document.createElement('div');
  divider.setAttribute(
    'class',
    'divider text-neutral-800 p-7 border rounded-sm mt-10 lg:mt-14 bg-pacific-blue/10 border-pacific-blue'
  );
  divider.innerHTML = `
      <p>
        The below results are non-affiliated Community Providers. The
        Providers are associated with private practices and have
        admitting privileges to at least one UR Medicine hospital. They
        are not employed by URMC.
      </p>
      `;

  // insert the divider before the first non-affiliated provider
  firstNonAffiliated?.closest('.provider-result-card')?.before(divider);

  // insert the border between the last affiliated provider and the divider
  if (lastAffiliatedBeforeDivider) {
    lastAffiliatedBeforeDivider.classList.add('border-b', 'pb-6');
  }
}

// used when retrieving facets with zero results
export function uniqBy(items: any[], property: string): any[] {
  const seen: any = {};

  return items.filter((item) => {
    const val = item[property];

    if (seen[val]) {
      return false;
    } else {
      seen[val] = true;
      return true;
    }
  });
}

export function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}
